import { suggestTemplate, suggestTrack } from '@/lib/leads/pinch-hit-outreach';
import type { Lead } from '@/types/leads';
import type {
  LeadOutreachEvent,
  LeadOutreachRollup,
  OutreachTemplateKey,
  OutreachTrack,
} from '@/types/lead-outreach';

export type WeekQueueSuggestionLead = Pick<Lead, 'id' | 'business_name' | 'dba' | 'solvable_pain_points'> &
  Partial<
    Pick<Lead, 'email' | 'contact_person' | 'notes' | 'qa_notes' | 'pos_vendor'>
  >;

function toSuggestionLead(lead: WeekQueueSuggestionLead): Lead {
  return {
    ...(lead as Lead),
    notes: lead.notes ?? null,
    qa_notes: lead.qa_notes ?? null,
    pos_vendor: lead.pos_vendor ?? null,
    email: lead.email ?? null,
    contact_person: lead.contact_person ?? null,
  };
}

function sortEventsNewestFirst(events: LeadOutreachEvent[]): LeadOutreachEvent[] {
  return [...events].sort(
    (left, right) => new Date(right.occurred_at).getTime() - new Date(left.occurred_at).getTime()
  );
}

export function assignWeekQueueOutreach(
  lead: WeekQueueSuggestionLead,
  events: LeadOutreachEvent[] = []
): { track: OutreachTrack; template_key: OutreachTemplateKey } {
  const sorted = sortEventsNewestFirst(events);
  const existingTrack = sorted[0]?.track;
  const existingTemplate = sorted.find((event) => event.template_key)?.template_key as
    | OutreachTemplateKey
    | undefined;

  if (existingTrack && existingTemplate) {
    return { track: existingTrack, template_key: existingTemplate };
  }

  const suggestionLead = toSuggestionLead(lead);
  const track = existingTrack ?? suggestTrack(suggestionLead, sorted);
  const template_key = existingTemplate ?? suggestTemplate(suggestionLead, track, sorted);
  return { track, template_key };
}

export function leadNeedsWeekQueueOutreachAssignment(events: LeadOutreachEvent[]): boolean {
  const sorted = sortEventsNewestFirst(events);
  if (sorted.length === 0) return true;
  if (!sorted[0]?.track) return true;
  return !sorted.some((event) => Boolean(event.template_key));
}

/**
 * Fill Track / Template on rollups for week-queue leads that have no outreach history yet.
 * Display-only — durable assignment happens when the queue is written.
 */
export function enrichOutreachRollupsWithSuggestions(
  rollups: Map<string, LeadOutreachRollup>,
  leads: WeekQueueSuggestionLead[],
  events: LeadOutreachEvent[],
  queuedLeadIds: Set<string>
): Map<string, LeadOutreachRollup> {
  if (queuedLeadIds.size === 0) return rollups;

  const eventsByLead = new Map<string, LeadOutreachEvent[]>();
  for (const event of events) {
    if (!queuedLeadIds.has(event.lead_id)) continue;
    const list = eventsByLead.get(event.lead_id) ?? [];
    list.push(event);
    eventsByLead.set(event.lead_id, list);
  }

  const next = new Map(rollups);
  for (const lead of leads) {
    if (!queuedLeadIds.has(lead.id)) continue;
    const rollup = next.get(lead.id);
    if (!rollup) continue;
    if (rollup.track && rollup.latest_template_key) continue;

    const assignment = assignWeekQueueOutreach(lead, eventsByLead.get(lead.id) ?? []);
    next.set(lead.id, {
      ...rollup,
      track: rollup.track ?? assignment.track,
      latest_template_key: rollup.latest_template_key ?? assignment.template_key,
    });
  }

  return next;
}
